import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import type { Feature, Geometry, FeatureCollection } from 'geojson';
import type { PathOptions } from 'leaflet';
import { generateColorMap } from '../../utils/colorUtils';
import { useMapData, type AracProperties, type MapFeature, type CommunityOutlineFeature } from '../../hooks/useMapData';
import { holderColors } from './hooks/useLeaseFeatures';

// Colors for availability status
export const availabilityColors = {
  'Y': '#22c55e', // Green for Available (Y means Available)
  'N': '#ef4444', // Red for Not Available (N means Not Available)
  'Unknown': '#9ca3af' // Gray for unknown status
};
import { useMapStore } from '../../store/mapStore';
import L from 'leaflet';
import './MapDisplay.css';
import 'leaflet/dist/leaflet.css';

const getPropertyValue = (props: AracProperties, baseName: string): string => {
  // Try variations of the field name
  const variations = [
    baseName,
    baseName + ' ',
    baseName.trim(),
  ];

  // Log the property access attempt
  console.log('Trying to access property:', {
    baseName,
    variations,
    availableProps: Object.keys(props)
  });

  // Try each variation
  for (const variant of variations) {
    if (props[variant] != null) {
      const value = props[variant];
      console.log('Found value for', variant, ':', value);
      return value.trim();
    }
  }

  console.log('No value found for', baseName);
  return 'N/A';
};

const MapDisplay: React.FC = () => {
  const { features, communityOutlineFeatures, loading, error } = useMapData();
  const [colorMap, setColorMap] = React.useState<Record<string, string>>({});
  // Create a ref to store the GeoJSON layer
  const geoJsonLayerRef = React.useRef<L.GeoJSON | null>(null);
  // Add state for color mode - default to leaseholder
  const [colorMode, setColorMode] = React.useState<'leaseholder' | 'availability'>('leaseholder');

  // Define map bounds
  const bounds = L.latLngBounds(
    L.latLng(-14.6, 135.8), // Southwest corner
    L.latLng(-13.5, 137.4)  // Northeast corner
  );

  // Calculate center point
  const center = bounds.getCenter();

  // List of specific holder names we want to highlight
  const specificHolderNames = [
    'NTG-CEO HOUSING',
    'Anindilyakwa Housing Aboriginal Corporation (AHAC)',
    'East Arnhem Regional Council (EARC)',
    'NTG-IES (subsidiary of Power and Water Corporation-PWC)',
    'Anindilyakwa Land Council (ALC)',
    'NTG-Territory Families, Housing and Communities (TFHC)',
    'NTG- Department Corporate and Digital Development (DCDD)'
  ];

  // Create a ref to store the current map view
  const mapViewRef = useRef<{center: L.LatLng, zoom: number} | null>(null);

  const getFeatureStyle = (feature?: MapFeature): PathOptions => {
    if (!feature) {
      console.log('getFeatureStyle called with undefined feature');
      return { 
        fillColor: '#cccccc',
        color: '#ffffff',  // White outline
        weight: 2,
        opacity: 1,
        fillOpacity: 0.6
      };
    }
    
    // Different styling based on color mode
    if (colorMode === 'leaseholder') {
      // Get holder_name using the same method as the popup
      const holderName = getPropertyValue(feature.properties, 'ARAC_Sublease Holder');
      
      // Log for debugging
      console.log('Styling feature with holder_name:', {
        holderName,
        featureId: feature.properties['ARAC_Lot Id (Database) '] || 'unknown'
      });
      
      // If holder name is 'N/A', return style with no fill
      if (holderName === 'N/A') {
        return {
          weight: 2,
          opacity: 1,
          color: '#ffffff',  // White outline
          fillOpacity: 0  // No fill for lots without a holder name
        };
      }
      
      // Check if the holder name is in our specific list
      const isSpecificHolder = specificHolderNames.includes(holderName);
      const fillColor = isSpecificHolder ? holderColors[holderName] : holderColors['Other'];
      
      // Log the color selection
      console.log('Color selection:', {
        holderName,
        isSpecificHolder,
        fillColor
      });
      
      return {
        fillColor,
        color: '#ffffff',  // White outline
        weight: 2,
        opacity: 1,
        fillOpacity: 0.6
      };
    } else {
      // Availability mode
      // Enhanced debugging to understand why we're getting Unknown
      const availability = feature.properties.lot_avail;
      
      // Log detailed debugging information
      console.log('Styling feature with availability:', {
        availability,
        availabilityType: typeof availability,
        featureId: feature.properties['ARAC_Lot Id (Database) '] || 'unknown',
        allProperties: Object.keys(feature.properties),
        hasLotAvail: 'lot_avail' in feature.properties,
        rawValue: feature.properties.lot_avail
      });
      
      // In the database: 'Y' means Available, 'N' means Not Available
      // *** DEBUGGED AVAILABILITY COLORING LOGIC ***
      // Force log the raw availability value to understand what we're working with
      console.log('RAW AVAILABILITY VALUE:', {
        value: availability,
        type: typeof availability,
        stringified: JSON.stringify(availability),
        featureId: feature.properties['ARAC_Lot Id (Database) ']?.trim(),
        lotNo: feature.properties['ARAC_Lot No.']?.trim()
      });
      
      // Handle case-insensitive comparisons with extreme robustness
      let colorKey: 'Y' | 'N' | 'Unknown' = 'Unknown';
      
      // Handle any availability value, not just truthy ones (important for catching "N" values)
      if (availability !== undefined && availability !== null) {
        // Force to string and uppercase for absolute consistency
        const availUpper = String(availability).toUpperCase().trim();
        
        // Use exact comparison with no assumptions
        if (availUpper === 'Y') {
          colorKey = 'Y';
        } else if (availUpper === 'N') {
          colorKey = 'N';
        } else {
          colorKey = 'Unknown';
        }
        
        console.log('Determined color key with enhanced logic:', {
          originalValue: availability,
          stringValue: String(availability),
          upperCaseValue: availUpper,
          resultingColorKey: colorKey,
          featureId: feature.properties['ARAC_Lot Id (Database) ']?.trim(),
          lotNo: feature.properties['ARAC_Lot No.']?.trim()
        });
      } else {
        console.log('No availability value found, using Unknown for lot:', {
          featureId: feature.properties['ARAC_Lot Id (Database) ']?.trim(),
          lotNo: feature.properties['ARAC_Lot No.']?.trim()
        });
      }
      
      const fillColor = availabilityColors[colorKey];
      
      return {
        fillColor,
        color: '#ffffff',  // White outline
        weight: 2,
        opacity: 1,
        fillOpacity: 0.6
      };
    }
  };

  const createPopupContent = (props: AracProperties): string => {
    // Log the properties being used for popup
    console.log('Creating popup with properties:', props);

    // Get availability status with a default "Unknown" if not found
    // Using database values directly: 'Y' means Available, 'N' means Not Available
    // Handle case-insensitive matching for more robust behavior
    const availability = props.lot_avail;
    
    // Log popup availability value for debugging
    console.log('Popup availability value:', {
      rawValue: availability,
      type: typeof availability,
      propertyExists: 'lot_avail' in props,
      allProperties: Object.keys(props)
    });
    
    // Use the enhanced comparison logic, matching the style function
    let availabilityDisplay = 'Unknown';
    let colorClass = 'text-gray-500';
    
    // Log popup availability raw value for debugging
    console.log('POPUP RAW AVAILABILITY VALUE:', {
      value: availability,
      type: typeof availability,
      stringified: JSON.stringify(availability)
    });
    
    // Handle any value, not just truthy ones (to catch 'N' values)
    if (availability !== undefined && availability !== null) {
      // Force to string and uppercase for absolute consistency
      const availUpper = String(availability).toUpperCase().trim();
      
      if (availUpper === 'Y') {
        availabilityDisplay = 'Available';
        colorClass = 'text-green-600 font-semibold';
      } else if (availUpper === 'N') {
        availabilityDisplay = 'Not Available';
        colorClass = 'text-red-600 font-semibold';
      }
    }

    return `
      <div class="custom-popup">
        <h3>${getPropertyValue(props, 'ARAC_Lot No.')}</h3>
        <p><strong>Holder:</strong> ${getPropertyValue(props, 'ARAC_Sublease Holder')}</p>
        <!-- Availability information hidden until data issues are resolved -->
        <!-- <p><strong>Availability:</strong> <span class="${colorClass}">${availabilityDisplay}</span></p> -->
        <p><strong>Sub-Lease Expiry:</strong> ${getPropertyValue(props, 'ARAC_Sublease Expiry')}</p>
        <p><strong>Zone:</strong> ${getPropertyValue(props, 'ARAC_Zone')}</p>
        <p><strong>Description:</strong> ${getPropertyValue(props, 'ARAC_Description Type')}</p>
      </div>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-lg">Loading map data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-lg text-red-600">
          Error loading map: {error}
          <br />
          <span className="text-sm text-gray-600">Check browser console for details</span>
        </div>
      </div>
    );
  }

  console.log('Rendering map with:', {
    featuresCount: features.length,
    colorMapKeys: Object.keys(colorMap),
    bounds: bounds.toBBoxString()
  });

  const geoJsonFeatures: FeatureCollection<Geometry, AracProperties> = {
    type: 'FeatureCollection',
    features
  };

  // Custom hook to handle the legend toggle
  const MapLegendController = () => {
    const map = useMap();
    const [showLegend, setShowLegend] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    // Check if device is mobile on component mount and window resize
    useEffect(() => {
      const checkIfMobile = () => {
        setIsMobile(window.innerWidth < 768); // Standard mobile breakpoint
      };
      
      // Initial check
      checkIfMobile();
      
      // Add event listener for window resize
      window.addEventListener('resize', checkIfMobile);
      
      // Cleanup
      return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Set initial legend visibility based on device type
    useEffect(() => {
      setShowLegend(!isMobile);
    }, [isMobile]);

    // Function to toggle legend while preserving map view
    const toggleLegend = useCallback(() => {
      if (map) {
        // Save current map view
        mapViewRef.current = {
          center: map.getCenter(),
          zoom: map.getZoom()
        };
        
        // Toggle legend
        setShowLegend(prev => !prev);
        
        // Use setTimeout to ensure the view is set after the state update
        setTimeout(() => {
          if (mapViewRef.current) {
            map.setView(mapViewRef.current.center, mapViewRef.current.zoom);
          }
        }, 0);
      } else {
        // Fallback if map is not available
        setShowLegend(prev => !prev);
      }
    }, [map]);

    return (
      <div className="absolute bottom-8 right-8 z-[1000]">
        {/* Colour By selector removed - hardcoded to leaseholder */}
        
        {/* Toggle button - always visible */}
        <button 
          onClick={toggleLegend}
          className="bg-white p-2 rounded-lg shadow-lg mb-2 w-full flex items-center justify-center"
        >
          {showLegend ? 'Hide Legend' : 'Show Legend'}
        </button>
        
        {/* Legend content - conditionally visible */}
        {showLegend && (
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <h3 className="text-sm font-semibold mb-2">Lease Holders</h3>
            <div className="space-y-2">
              {Object.entries(holderColors).map(([name, color]) => (
                <div key={name} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm">{name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Create a controller component to handle map view changes
  const MapViewController = () => {
    const map = useMap();
    const selectedLotId = useMapStore(state => state.selectedLotId);
    
    useEffect(() => {
      // If no lot is selected, use the default bounds
      if (!selectedLotId) {
        map.fitBounds(bounds);
        return;
      }
      
      // Log the selected lot ID
      console.log('Selected lot ID:', selectedLotId);
      
      // Log the first few features' lot IDs for comparison
      if (features.length > 0) {
        console.log('Sample feature lot IDs:', features.slice(0, 5).map(f => ({
          'ARAC_Lot Id (Database)': f.properties['ARAC_Lot Id (Database) ']?.trim(),
          'ARAC_Lot No.': f.properties['ARAC_Lot No.']?.trim()
        })));
      }
      
      // Find the feature with the matching lot ID
      let selectedFeature = features.find(feature => {
        // Get the database ID and lot number
        const databaseId = feature.properties['ARAC_Lot Id (Database) ']?.trim();
        const lotNo = feature.properties['ARAC_Lot No.']?.trim();
        
        // Case-insensitive direct match
        if (databaseId && selectedLotId && 
            databaseId.toLowerCase() === selectedLotId.toLowerCase()) {
          console.log('Exact match found (case-insensitive):', { databaseId, selectedLotId });
          return true;
        }
        
        // Try to match by extracting the lot number from both IDs
        if (selectedLotId && databaseId) {
          // Extract lot number from selectedLotId (after the dash)
          const selectedLotParts = selectedLotId.split('-');
          const selectedLotNumber = selectedLotParts[selectedLotParts.length - 1];
          
          // Extract lot number from databaseId (after the dash)
          const databaseIdParts = databaseId.split('-');
          const databaseLotNumber = databaseIdParts[databaseIdParts.length - 1];
          
          // Case-insensitive community code match
          const selectedCommunityCode = selectedLotParts[0].toLowerCase();
          const databaseCommunityCode = databaseIdParts[0].toLowerCase();
          
          // Match if community codes and lot numbers are the same (case-insensitive)
          if (selectedCommunityCode === databaseCommunityCode && 
              selectedLotNumber === databaseLotNumber) {
            console.log('Matched by community code and lot number:', {
              selectedLotId,
              databaseId,
              selectedCommunityCode,
              databaseCommunityCode,
              selectedLotNumber,
              databaseLotNumber
            });
            return true;
          }
          
          // Match if just lot numbers are the same
          if (selectedLotNumber === databaseLotNumber || selectedLotNumber === lotNo) {
            console.log('Matched by lot number only:', {
              selectedLotId,
              databaseId,
              lotNo,
              selectedLotNumber,
              databaseLotNumber
            });
            return true;
          }
        }
        
        return false;
      });

      // If no feature is found, try a broader search
      if (!selectedFeature && selectedLotId) {
        console.log('No exact match found, trying broader search...');
        
        // Extract lot number from selectedLotId (after the dash)
        const selectedLotParts = selectedLotId.split('-');
        const selectedLotNumber = selectedLotParts[selectedLotParts.length - 1];
        
        // Try to find any feature with this lot number
        const lotNumberFeature = features.find(feature => {
          const lotNo = feature.properties['ARAC_Lot No.']?.trim();
          return lotNo === selectedLotNumber;
        });
        
        if (lotNumberFeature) {
          console.log('Found feature by lot number only:', {
            selectedLotId,
            lotNo: lotNumberFeature.properties['ARAC_Lot No.']?.trim(),
            databaseId: lotNumberFeature.properties['ARAC_Lot Id (Database) ']?.trim()
          });
          selectedFeature = lotNumberFeature;
        }
      }
      
      if (selectedFeature) {
        try {
          // Log the matched feature for debugging
          console.log('Found matching feature:', {
            'ARAC_Lot Id (Database)': selectedFeature.properties['ARAC_Lot Id (Database) ']?.trim(),
            'ARAC_Lot No.': selectedFeature.properties['ARAC_Lot No.']?.trim(),
            selectedLotId
          });
          
          // Convert GeoJSON to Leaflet layer
          const layer = L.geoJSON(selectedFeature);
          
          // Get the center point of the feature
          const center = layer.getBounds().getCenter();
          
          console.log('Centering map on feature:', {
            center: center.toString(),
            lotId: selectedLotId
          });
          
          // Set the view to this center point with maximum zoom
          map.setView(center, 16); // Use a high zoom level (16)
          
          // Force a map invalidation to ensure the view change is applied
          setTimeout(() => {
            map.invalidateSize();
            
            // Highlight the selected feature
            layer.setStyle({
              weight: 3,
              color: '#ff7800',
              fillOpacity: 0.7
            });
            
            // DIRECT POPUP APPROACH: Iterate through all layers in the GeoJSON layer
            if (geoJsonLayerRef.current) {
              let popupOpened = false;
              
              geoJsonLayerRef.current.eachLayer((mapLayer: any) => {
                // Check if this layer corresponds to our selected feature
                if (!popupOpened && mapLayer.feature && mapLayer.feature.properties) {
                  const layerDatabaseId = mapLayer.feature.properties['ARAC_Lot Id (Database) ']?.trim();
                  const layerLotNo = mapLayer.feature.properties['ARAC_Lot No.']?.trim();
                  
                  // Try different matching strategies
                  const isMatch = 
                    // Exact match
                    (layerDatabaseId === selectedLotId) ||
                    // Case-insensitive match
                    (layerDatabaseId?.toLowerCase() === selectedLotId?.toLowerCase()) ||
                    // Lot number only match
                    (selectedLotId?.includes('-') && 
                     layerDatabaseId?.includes('-') &&
                     selectedLotId.split('-')[1] === layerDatabaseId.split('-')[1]) ||
                    // Direct lot number match
                    (selectedLotId?.includes('-') && 
                     layerLotNo === selectedLotId.split('-')[1]);
                  
                  if (isMatch) {
                    console.log('Opening popup for matching layer:', {
                      layerDatabaseId,
                      layerLotNo,
                      selectedLotId
                    });
                    
                    // Open the popup
                    if (mapLayer.getPopup) {
                      mapLayer.openPopup();
                      popupOpened = true;
                    }
                  }
                }
              });
              
              if (!popupOpened) {
                console.warn('Could not find a layer to open popup for:', selectedLotId);
              }
            } else {
              console.warn('GeoJSON layer reference not available');
            }
          }, 500); // Increased timeout for more reliability
        } catch (error) {
          console.error('Error zooming to feature:', error);
          map.fitBounds(bounds);
        }
      } else {
        console.warn('Selected lot not found in features:', selectedLotId);
        map.fitBounds(bounds);
      }
    }, [map, selectedLotId]);
    
    return null;
  };

  return (
    <div className="relative w-full h-full z-0">
      <MapContainer
        center={center}
        bounds={bounds}
        maxBounds={bounds}
        maxBoundsViscosity={1.0}
        zoom={11}
        className="h-full w-full"
        maxZoom={17}
        minZoom={10}
      >
        <MapViewController />
        <MapLegendController />
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
          maxZoom={17}
        />
        
        {/* Community outlines layer - rendered FIRST so it appears BELOW the lots */}
        {communityOutlineFeatures && (
          <GeoJSON
            data={{
              type: 'FeatureCollection',
              features: communityOutlineFeatures
            } as FeatureCollection}
            style={{
              color: 'orange',
              weight: 3,
              fillOpacity: 0.1, // Slight opacity to make it clickable
              fillColor: 'orange'
            }}
            onEachFeature={(feature: CommunityOutlineFeature, layer) => {
              // Add popup with community name
              if (feature.properties && feature.properties.communityName) {
                const communityName = feature.properties.communityName;
                layer.bindPopup(`<div class="custom-popup"><h3>${communityName} Community</h3></div>`);
              }
            }}
          />
        )}
        
        {/* Lots layer - rendered SECOND so it appears ABOVE the community outlines */}
        <GeoJSON
          ref={(layer) => {
            if (layer) {
              geoJsonLayerRef.current = layer;
            }
          }}
          data={geoJsonFeatures}
          style={getFeatureStyle}
          onEachFeature={(feature, layer) => {
            if (feature.properties) {
              const popupContent = createPopupContent(feature.properties);
              layer.bindPopup(popupContent);
              
              // Add lot ID as a property to the layer for easier identification
              if (feature.properties['ARAC_Lot Id (Database) ']) {
                const lotId = feature.properties['ARAC_Lot Id (Database) '].trim();
                (layer as any).lotId = lotId;
              }
              
              // Bring this layer to front to ensure it appears above community outlines
              // Use type assertion to handle TypeScript error
              (layer as any).bringToFront?.();
            }
          }}
        />
      </MapContainer>
    </div>
  );
};

export default MapDisplay;
